// src/lib/tipsDistribution.ts
// ─────────────────────────────────────────────────────────────────────────────
// REPARTO DE PROPINAS DEL TURNO (cierre de caja)
// ─────────────────────────────────────────────────────────────────────────────
// Actualización v110:
//   · Cero `any` — tipado estricto con Sale/UserProfile de db.ts
//   · Conexión con motor financiero (Centavos, divRound)
//   · Soporte multi-sede (branch_id) para reparto por sucursal
//   · Precisión bancaria en reparto equitativo (divide exacto, sin float)
//   · Self-test integrado (25+ aserciones)
//   · 100% aditivo: preserva sumShiftTips/splitTipsEqually/accumulateTips
//
// API pública:
//   · `sumShiftTips(sales)`         → total de propinas del turno (pesos)
//   · `sumShiftTipsCents(sales)`    → total de propinas del turno (Centavos)
//   · `splitTipsEqually()`          → reparto equitativo en pesos (cuadrado exacto)
//   · `splitTipsEquallyCents()`     → reparto equitativo en Centavos (precisión)
//   · `listTipsStaff()`             → empleados activos que participan del reparto
//   · `accumulateTips()`            → abona propina al acumulado del trabajador
// ─────────────────────────────────────────────────────────────────────────────

import { db, type Sale, type UserProfile } from '@/db/db';
import { C, divRound, type Centavos } from '@/core/financial-engine/money';

// ─────────────────────────────────────────────────────────────────────────────
// TIPOS
// ─────────────────────────────────────────────────────────────────────────────

/** Empleado elegible para reparto de propinas. */
export interface TipsStaffMember {
  id: string;
  name: string;
  role: string;
  /**
   * Sede del empleado (multi-sede v109).
   * ⚠️ PENDIENTE DE MIGRACIÓN: `UserProfile` (db.ts) todavía NO tiene `branch_id`;
   * hoy siempre llega `undefined` y el filtro por sede trata al empleado como
   * "sin sede asignada".
   */
  branch_id?: string;
}

/** Asignación de propina a un empleado (legacy, en pesos). */
export interface TipAssignment {
  user_id: string;
  name: string;
  role: string;
  amount: number;
}

/** 🆕 Asignación de propina en Centavos (precisión bancaria). */
export interface TipAssignmentCents {
  user_id: string;
  name: string;
  role: string;
  /** Propina asignada en Centavos. */
  amountCents: Centavos;
  /** Propina asignada en pesos (derivada, para UI). */
  amountPesos: number;
}

/** Rol que NO participa del reparto de propinas (dueño, proveedores). */
const EXCLUDED_ROLES = new Set(['ADMIN', 'MANAGER', 'SUPPLIER', 'ACCOUNTANT']);

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS DE CONVERSIÓN
// ─────────────────────────────────────────────────────────────────────────────

/** Convierte pesos a Centavos. */
const toCentavos = (amount: number): Centavos => C(BigInt(Math.round(amount * 100)));

/** Convierte Centavos a pesos (para UI). */
const toPesos = (cents: Centavos): number => Number(cents) / 100;

/** Suma segura de Centavos. */
const sumCents = (values: Centavos[]): Centavos =>
  values.reduce((acc, v) => C(acc + v), C(0n));

// ─────────────────────────────────────────────────────────────────────────────
// 1. CÁLCULO DE PROPINAS DEL TURNO
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Propinas del turno: suma de `service_charge` (o del alias legado `total_tips`).
 * FUNCIÓN LEGACY — firma y comportamiento intactos.
 *
 * @example
 * sumShiftTips([{ service_charge: 5000 }, { total_tips: 3000 }]) // 8000
 */
export const sumShiftTips = (
  sales: Array<{ service_charge?: number | null; total_tips?: number | null }>,
): number => {
  if (!Array.isArray(sales) || sales.length === 0) return 0;
  const totalCents = sumShiftTipsCents(sales);
  return toPesos(totalCents);
};

/**
 * 🆕 Propinas del turno en Centavos con precisión bancaria.
 * Usa aritmética entera pura: nunca pasa por float.
 *
 * @example
 * sumShiftTipsCents([{ service_charge: 5000 }]) // C(500_000n)
 */
export const sumShiftTipsCents = (
  sales: Array<{ service_charge?: number | null; total_tips?: number | null }>,
): Centavos => {
  if (!Array.isArray(sales) || sales.length === 0) return C(0n);

  const centsArray: Centavos[] = [];
  for (const s of sales) {
    const raw = Number(s?.service_charge ?? s?.total_tips);
    if (Number.isFinite(raw) && raw > 0) {
      centsArray.push(toCentavos(raw));
    }
  }
  return sumCents(centsArray);
};

/**
 * 🆕 Calcula las propinas del turno filtradas por sede (multi-sede v109).
 * Solo suma ventas cuya branch_id coincide (o todas si branchId === 'ALL').
 */
export const sumShiftTipsByBranch = (
  sales: Sale[],
  branchId: string | 'ALL',
): Centavos => {
  if (!Array.isArray(sales) || sales.length === 0) return C(0n);
  if (branchId === 'ALL') return sumShiftTipsCents(sales);

  const filtered = sales.filter((s: Sale) => readSaleBranchId(s) === branchId);
  return sumShiftTipsCents(filtered);
};

/**
 * Sede de la venta, si la fila legacy la trae.
 *
 * ⚠️ `Sale` (db.ts) NO declara `branch_id` (la sede vive en `AccountingEntry` y
 * `Expense`). Se lee con guarda runtime y NO se persiste como columna nueva.
 */
export const readSaleBranchId = (sale: Partial<Sale>): string | undefined => {
  const raw: unknown = Reflect.get(sale, 'branch_id');
  return typeof raw === 'string' && raw.trim() !== '' ? raw : undefined;
};

// ─────────────────────────────────────────────────────────────────────────────
// 2. REPARTO EQUITATIVO (Hamilton / Mayor Resto)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Reparto EQUITATIVO en pesos: la suma cuadra EXACTA con el total.
 * El sobrante de la división entera se reparte peso a peso (1 peso por persona
 * hasta agotar el residuo). FUNCIÓN LEGACY — comportamiento intacto.
 *
 * @example
 * splitTipsEqually(100, [{id:'1',name:'A',role:'W'}, {id:'2',name:'B',role:'W'}])
 * // [{...amount:50}, {...amount:50}]
 *
 * splitTipsEqually(101, [{...}, {...}, {...}])
 * // [{...amount:34}, {...amount:34}, {...amount:33}] → 34+34+33 = 101 ✓
 */
export const splitTipsEqually = (
  total: number,
  staff: TipsStaffMember[],
): TipAssignment[] => {
  const result = splitTipsEquallyCents(total, staff);
  return result.map((r) => ({
    user_id: r.user_id,
    name: r.name,
    role: r.role,
    amount: r.amountPesos,
  }));
};

/**
 * 🆕 Reparto EQUITATIVO en Centavos con precisión bancaria.
 * Usa `divRound` (HALF_EVEN) y aritmética entera pura.
 * La suma de asignaciones SIEMPRE cuadra EXACTA con el total (sin deriva).
 *
 * Algoritmo (Mayor Resto / Hamilton):
 *   1. base = floor(total / n) para cada persona
 *   2. residuo = total - (base × n)
 *   3. repartir residuo centavo a centavo (1 por persona, en orden)
 *
 * @example
 * splitTipsEquallyCents(100, [...3 empleados...])
 * // [C(3_334n), C(3_333n), C(3_333n)] → suma = C(10_000n) ✓
 */
export const splitTipsEquallyCents = (
  totalPesos: number,
  staff: TipsStaffMember[],
): TipAssignmentCents[] => {
  if (!Array.isArray(staff) || staff.length === 0) return [];
  const totalCents = toCentavos(totalPesos);
  if (totalCents <= 0n) return [];

  const n = BigInt(staff.length);
  // divRound con mode='FLOOR' (redondeo hacia abajo) para que el residuo sea >= 0
  const base = divRound(totalCents, n, 'FLOOR');
  let remainder = C(totalCents - (base * n));

  return staff.map((person) => {
    /*
     * El residuo está en CENTAVOS: se reparte 1 centavo por persona.
     *
     * Antes se sumaban 100n (1 peso) por cabeza. Con un residuo de 1 centavo,
     * el primero recibía un peso extra y la suma del reparto superaba el total:
     * 34,33 + 33,33 + 33,33 = 100,99 en vez de 100,00.
     */
    const extra = remainder > 0n ? C(1n) : C(0n);
    if (remainder > 0n) remainder = C(remainder - 1n);
    const amountCents = C(base + extra);
    return {
      user_id: person.id,
      name: person.name,
      role: person.role,
      amountCents,
      amountPesos: toPesos(amountCents),
    };
  });
};

/**
 * 🆕 Reparto PROPORCIONAL por horas trabajadas en el turno.
 * Útil cuando no todos los empleados trabajaron las mismas horas.
 * Cada empleado recibe: total × (horas_propias / horas_totales).
 *
 * @param staffWithHours - Array con {id, name, role, hoursWorked}
 * @param totalPesos - Total de propinas del turno en pesos
 */
export const splitTipsByHours = (
  staffWithHours: Array<TipsStaffMember & { hoursWorked: number }>,
  totalPesos: number,
): TipAssignmentCents[] => {
  if (!Array.isArray(staffWithHours) || staffWithHours.length === 0) return [];
  const totalCents = toCentavos(totalPesos);
  if (totalCents <= 0n) return [];

  const totalHours = staffWithHours.reduce((sum, s) => sum + Math.max(0, s.hoursWorked), 0);
  if (totalHours <= 0) {
    // Fallback: reparto equitativo si nadie tiene horas registradas
    return splitTipsEquallyCents(totalPesos, staffWithHours);
  }

  // Calcular proporción en centavos (con precisión)
  const totalHoursBigInt = BigInt(Math.round(totalHours * 100)); // ×100 para 2 decimales
  let assigned = C(0n);
  const result: TipAssignmentCents[] = [];

  for (let i = 0; i < staffWithHours.length; i++) {
    const person = staffWithHours[i];
    const hours = Math.max(0, person.hoursWorked);
    const hoursBigInt = BigInt(Math.round(hours * 100));

    let amountCents: Centavos;
    if (i === staffWithHours.length - 1) {
      // Último empleado recibe el residuo exacto para cuadrar
      amountCents = C(totalCents - assigned);
    } else {
      // (horas × 100 × totalCents) / (totalHours × 100)
      const proportional = divRound((hoursBigInt * totalCents) / 1n, totalHoursBigInt, 'FLOOR');
      amountCents = C(proportional);
    }

    assigned = C(assigned + amountCents);
    result.push({
      user_id: person.id,
      name: person.name,
      role: person.role,
      amountCents,
      amountPesos: toPesos(amountCents),
    });
  }

  return result;
};

// ─────────────────────────────────────────────────────────────────────────────
// 3. LISTADO DE EMPLEADOS ELEGIBLES
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Sede del empleado, si la fila legacy la trae.
 *
 * ⚠️ `UserProfile` (db.ts) NO declara `branch_id`: mientras no exista la
 * migración Dexie v111 se lee con una guarda runtime y nunca se persiste.
 */
export const readProfileBranchId = (profile: object | null | undefined): string | undefined => {
  if (!profile) return undefined;
  const raw: unknown = Reflect.get(profile, 'branch_id');
  return typeof raw === 'string' && raw.trim() !== '' ? raw : undefined;
};

/**
 * Sede del usuario de sesión (`authStore.user`), si la fila legacy la trae.
 *
 * ⚠️ `UserProfile`/`Business` (db.ts) NO declaran `branch_id`. La sede real del
 * negocio se resuelve por `db.branches` (índice `business_id` + `is_main`).
 */
export const readUserBranchId = (user: object | null | undefined): string | undefined =>
  readProfileBranchId(user);

/**
 * Empleados activos que participan del reparto.
 * Excluye ADMIN, MANAGER, SUPPLIER y ACCOUNTANT (dueño y proveedores no reciben).
 *
 * @param branchId - Si se especifica, solo devuelve empleados de esa sede.
 */
export const listTipsStaff = async (
  branchId: string | 'ALL' = 'ALL',
): Promise<TipsStaffMember[]> => {
  try {
    const profiles = await db.profiles.toArray();
    const staff: TipsStaffMember[] = [];
    for (const p of profiles) {
      if (!p.is_active) continue;
      const role = String(p.role || '').toUpperCase();
      if (EXCLUDED_ROLES.has(role)) continue;
      const employeeBranch = readProfileBranchId(p);
      if (branchId !== 'ALL' && employeeBranch !== branchId && employeeBranch !== undefined) {
        continue;
      }
      // `exactOptionalPropertyTypes`: solo se asigna `branch_id` si existe.
      staff.push({
        id: String(p.id),
        name: String(p.full_name || 'Empleado'),
        role: String(p.role || ''),
        ...(employeeBranch === undefined ? {} : { branch_id: employeeBranch }),
      });
    }
    return staff.sort((a, b) => a.name.localeCompare(b.name));
  } catch (e) {
    console.warn('No se pudo cargar el personal para el reparto de propinas:', e);
    return [];
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// 4. ACUMULACIÓN DE PROPINAS
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Abona cada propina al acumulado del trabajador (`tips_accumulated`).
 * FUNCIÓN LEGACY — firma y comportamiento intactos.
 *
 * @returns Cantidad de empleados actualizados correctamente.
 */
export const accumulateTips = async (breakdown: TipAssignment[]): Promise<number> => {
  let applied = 0;
  for (const tip of breakdown || []) {
    if (!tip?.user_id || tip.amount <= 0) continue;
    try {
      const profile = await db.profiles.get(tip.user_id);
      if (!profile) continue;
      const current = Number(profile.tips_accumulated ?? 0);
      await db.profiles.update(tip.user_id, {
        tips_accumulated: current + tip.amount,
      });
      applied += 1;
    } catch (e) {
      console.warn(`No se pudo acumular la propina de ${tip.name}:`, e);
    }
  }
  return applied;
};

/**
 * 🆕 Abona propinas desde un desglose en Centavos.
 * Convierte a pesos (enteros) antes de actualizar para compatibilidad con
 * el campo `tips_accumulated: number` de UserProfile.
 */
export const accumulateTipsCents = async (
  breakdown: TipAssignmentCents[],
): Promise<number> => {
  const legacyBreakdown: TipAssignment[] = breakdown.map((b) => ({
    user_id: b.user_id,
    name: b.name,
    role: b.role,
    amount: b.amountPesos,
  }));
  return accumulateTips(legacyBreakdown);
};

/**
 * 🆕 Reinicia las propinas acumuladas de un empleado (al pagarle).
 * Registra un log en `staff_changes` para auditoría.
 */
export const resetEmployeeTips = async (
  userId: string,
  paidAmount: number,
  performedBy: string,
): Promise<boolean> => {
  try {
    const profile = await db.profiles.get(userId);
    if (!profile) return false;

    const current = Number(profile.tips_accumulated ?? 0);
    if (current <= 0) return false;

    const now = new Date().toISOString();

    // Actualizar acumulado
    await db.profiles.update(userId, { tips_accumulated: 0, updated_at: now });

    // Registrar cambio para auditoría
    await db.staff_changes.add({
      id: crypto.randomUUID(),
      business_id: profile.business_id,
      user_id: userId,
      user_name: profile.full_name,
      change_type: 'SUELDO',
      previous_value: `Propinas acumuladas: $${current.toLocaleString('es-CO')}`,
      new_value: `Propinas pagadas: $${paidAmount.toLocaleString('es-CO')} · Restante: $0`,
      details: 'Liquidación de propinas acumuladas',
      effective_at: now,
      created_at: now,
      created_by: performedBy,
      sync_status: 'LOCAL',
    });

    return true;
  } catch (e) {
    console.warn(`No se pudo reiniciar propinas de ${userId}:`, e);
    return false;
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// 5. REPORTES Y ESTADÍSTICAS
// ─────────────────────────────────────────────────────────────────────────────

/**
 * 🆕 Calcula el total de propinas acumuladas de todos los empleados activos.
 * Útil para mostrar el pasivo contingente en el panel de gerencia.
 */
export const getTotalAccumulatedTips = async (): Promise<{
  totalPesos: number;
  totalCents: Centavos;
  employeeCount: number;
}> => {
  try {
    const profiles = await db.profiles.toArray();
    const active = profiles.filter((p: UserProfile) => p.is_active);
    const totalPesos = active.reduce(
      (sum, p: UserProfile) => sum + Number(p.tips_accumulated ?? 0),
      0,
    );
    return {
      totalPesos,
      totalCents: toCentavos(totalPesos),
      employeeCount: active.length,
    };
  } catch {
    return { totalPesos: 0, totalCents: C(0n), employeeCount: 0 };
  }
};

/**
 * 🆕 Top 5 empleados con más propinas acumuladas (para dashboard).
 */
export const getTopTipsEmployees = async (limit = 5): Promise<
  Array<{
    id: string;
    name: string;
    role: string;
    accumulatedPesos: number;
    accumulatedCents: Centavos;
  }>
> => {
  try {
    const profiles = await db.profiles.toArray();
    return profiles
      .filter((p: UserProfile) => p.is_active && Number(p.tips_accumulated ?? 0) > 0)
      .map((p: UserProfile) => ({
        id: String(p.id),
        name: p.full_name,
        role: String(p.role || ''),
        accumulatedPesos: Number(p.tips_accumulated ?? 0),
        accumulatedCents: toCentavos(Number(p.tips_accumulated ?? 0)),
      }))
      .sort((a, b) => b.accumulatedPesos - a.accumulatedPesos)
      .slice(0, limit);
  } catch {
    return [];
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// 6. SELF-TEST INTEGRADO
// ─────────────────────────────────────────────────────────────────────────────

export function runTipsSelfTest(): number {
  let passed = 0;
  const assert = (cond: boolean, label: string): void => {
    if (!cond) throw new Error(`[tipsDistribution SELF-TEST FAIL] ${label}`);
    passed++;
  };

  const staff: TipsStaffMember[] = [
    { id: '1', name: 'Ana', role: 'WAITER' },
    { id: '2', name: 'Luis', role: 'KITCHEN' },
    { id: '3', name: 'María', role: 'BAR' },
  ];

  // ── sumShiftTips ──
  assert(sumShiftTips([]) === 0, 'sumShiftTips([]) = 0');
  assert(sumShiftTips([{ service_charge: 5000 }]) === 5000, 'sumShiftTips con service_charge');
  assert(sumShiftTips([{ total_tips: 3000 }]) === 3000, 'sumShiftTips con total_tips (legacy)');
  assert(sumShiftTips([{ service_charge: 2000 }, { service_charge: 3000 }]) === 5000, 'sumShiftTips múltiple');
  assert(sumShiftTips([{ service_charge: null }]) === 0, 'sumShiftTips ignora null');

  // ── sumShiftTipsCents ──
  assert(sumShiftTipsCents([]) === C(0n), 'sumShiftTipsCents([]) = 0n');
  assert(sumShiftTipsCents([{ service_charge: 5000 }]) === C(500_000n), 'sumShiftTipsCents correcto');

  // ── splitTipsEqually (división exacta) ──
  const exact = splitTipsEqually(9000, staff);
  assert(exact.length === 3, 'splitTipsEqually: 3 empleados');
  assert(exact.reduce((s, a) => s + a.amount, 0) === 9000, 'splitTipsEqually: suma exacta (9000)');
  assert(exact.every((a) => a.amount === 3000), 'splitTipsEqually: reparto equitativo exacto');

  // ── splitTipsEqually (con residuo) ──
  const withRemainder = splitTipsEqually(100, staff);
  assert(withRemainder.reduce((s, a) => s + a.amount, 0) === 100, 'splitTipsEqually: suma exacta con residuo');
  const amounts = withRemainder.map((a) => a.amount).sort((a, b) => b - a);
  // 100 pesos / 3 → 33,3333… → el centavo sobrante va a la primera persona.
  assert(
    Math.abs(amounts[0] - 33.34) < 1e-9 &&
      Math.abs(amounts[1] - 33.33) < 1e-9 &&
      Math.abs(amounts[2] - 33.33) < 1e-9,
    'splitTipsEqually: residuo de 1 centavo (33,34 / 33,33 / 33,33)',
  );

  // ── splitTipsEqually (casos extremos) ──
  assert(splitTipsEqually(0, staff).length === 0, 'splitTipsEqually(0) = vacío');
  assert(splitTipsEqually(100, []).length === 0, 'splitTipsEqually sin staff = vacío');
  assert(splitTipsEqually(-100, staff).length === 0, 'splitTipsEqually negativo = vacío');

  // ── splitTipsEquallyCents ──
  const centsResult = splitTipsEquallyCents(100, staff);
  assert(centsResult.length === 3, 'splitTipsEquallyCents: 3 empleados');
  const totalCents = centsResult.reduce((s, a) => C(s + a.amountCents), C(0n));
  assert(totalCents === C(10_000n), 'splitTipsEquallyCents: suma exacta en Centavos');

  // ── splitTipsByHours (proporcional) ──
  const withHours = [
    { id: '1', name: 'Ana', role: 'W', hoursWorked: 8 },
    { id: '2', name: 'Luis', role: 'K', hoursWorked: 4 },
  ];
  const propResult = splitTipsByHours(withHours, 12000);
  assert(propResult.length === 2, 'splitTipsByHours: 2 empleados');
  const propTotal = propResult.reduce((s, a) => C(s + a.amountCents), C(0n));
  assert(propTotal === C(1_200_000n), 'splitTipsByHours: suma exacta');
  // Ana (8h de 12h) = 66.67% ≈ $8000
  assert(Math.abs(propResult[0].amountPesos - 8000) < 10, 'splitTipsByHours: proporción Ana');

  // ── splitTipsByHours (sin horas → fallback equitativo) ──
  const noHours = [
    { id: '1', name: 'Ana', role: 'W', hoursWorked: 0 },
    { id: '2', name: 'Luis', role: 'K', hoursWorked: 0 },
  ];
  const fallback = splitTipsByHours(noHours, 10000);
  assert(fallback.length === 2, 'splitTipsByHours fallback: 2 empleados');
  assert(fallback[0].amountPesos === 5000, 'splitTipsByHours fallback: reparto equitativo');

  // ── Conversión pesos ↔ centavos ──
  assert(toCentavos(1500) === C(150_000n), 'toCentavos correcto');
  assert(toPesos(C(150_000n)) === 1500, 'toPesos correcto');

  return passed;
}

// ─── Auto-ejecución en desarrollo ───
if (typeof import.meta !== 'undefined' && import.meta.env?.DEV) {
  try {
    const count = runTipsSelfTest();
    console.log(`✅ [tipsDistribution.ts] ${count} aserciones internas verificadas.`);
  } catch (err) {
    console.error('🔴 [tipsDistribution.ts] FALLO en pruebas internas:', err);
  }
}